import {useEffect, useState} from "react";

/**
 * True when the viewport is at least `px` wide. Backed by matchMedia and kept in sync on resize.
 *
 * Used to GATE RENDERING of the wide-screen-only ad rails: `adsbygoogle.push({})` fills the next
 * unprocessed <ins> in DOM order, so a hidden (display:none) rail left in the DOM steals the push
 * meant for a visible banner and errors with availableWidth=0. Only mounting the rail's <ins> when
 * it's actually visible keeps the push queue aligned with what the user can see.
 */
export function useMinWidth(px: number): boolean {
    const query = `(min-width: ${px}px)`;
    // Always start FALSE — matching the server-rendered HTML (which has no viewport). Reading
    // matchMedia in the initializer made the first client render differ from SSR on wide screens
    // (rails present client-side, absent server-side) → React hydration error #418 on every
    // public page for desktop visitors. The effect below syncs the real value right after mount.
    const [matches, setMatches] = useState<boolean>(false);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const mq = window.matchMedia(query);
        const onChange = () => setMatches(mq.matches);
        onChange(); // sync immediately in case it changed between render and effect
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, [query]);

    return matches;
}
